import React, { forwardRef, RefObject } from 'react';
import { Slide } from 'react-toastify';

import { Comp } from '@redsift/design-system';
import { ReactToastify, StyledToastContainer } from './styles';
import { ToastContainerProps } from './types';

const COMPONENT_NAME = 'ToastContainer';
const CLASSNAME = 'redsift-toast-container';

/**
 * ToastContainer provides the root element for toast notifications.
 * Place it once at the app root. Raise toasts with the `useToast` hook, or with
 * the `toast` singleton when you are outside a component.
 *
 * Placements: `top-left`, `top-center`, `top-right`, `bottom-left`,
 * `bottom-center`, `bottom-right`
 *
 * @example
 * // Setup in app root (required once)
 * import { ToastContainer } from '@redsift/popovers';
 * function App() {
 *   return (
 *     <>
 *       <YourApp />
 *       <ToastContainer />
 *     </>
 *   );
 * }
 *
 * @example
 * // Using the useToast hook in a component
 * import { useToast } from '@redsift/popovers';
 * const { notify, success, error, warning, info, loading } = useToast();
 *
 * // Show different variants
 * success({ content: 'Changes saved!' });
 * error({ content: 'Failed to save', title: 'Error' });
 * info({ content: 'New updates available' });
 * warning({ content: 'Your session is about to expire' });
 *
 * @example
 * // Promise-based toast (shows loading, then success/error)
 * const { promise } = useToast();
 * promise(fetchData(), {
 *   pending: { content: 'Loading data...' },
 *   success: { content: 'Data loaded!' },
 *   error: { content: 'Failed to load data' },
 * });
 *
 * @example
 * // Update or dismiss an existing toast. Options left out of an update keep
 * // the value they were last rendered with.
 * const { notify, update, dismiss } = useToast();
 * const toastId = notify({ content: 'Processing...', variant: 'loading' });
 * // Later:
 * update(toastId, { content: 'Done!', variant: 'success' });
 * // Or:
 * dismiss(toastId);
 *
 * @example
 * // Outside a component — a service, an interceptor, a store
 * import { toast } from '@redsift/popovers';
 * toast.error({ title: 'Error', content: 'Failed to save' });
 */
export const ToastContainer: Comp<ToastContainerProps, HTMLDivElement> = forwardRef((props, ref) => {
  const {
    autoClose = 5000,
    closeOnClick = false,
    draggable = true,
    pauseOnFocusLoss = true,
    pauseOnHover = true,
    placement = 'top-right',
    width,
    ...forwardedProps
  } = props;

  return (
    <ReactToastify>
      <StyledToastContainer
        autoClose={autoClose}
        closeOnClick={closeOnClick}
        draggable={draggable}
        pauseOnFocusLoss={pauseOnFocusLoss}
        pauseOnHover={pauseOnHover}
        {...forwardedProps}
        className={ToastContainer.className}
        closeButton={false}
        icon={false}
        newestOnTop={placement!.includes('bottom')}
        position={placement}
        ref={ref as RefObject<HTMLDivElement>}
        theme="colored"
        transition={Slide}
        $width={typeof width === 'number' ? `${width}px` : width}
      />
    </ReactToastify>
  );
});
ToastContainer.className = CLASSNAME;
ToastContainer.displayName = COMPONENT_NAME;
